import React, { useEffect, useState } from "react";
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import { useNavigate } from "react-router-dom";
import "./PaymentsReceived.css";

const PaymentsReceived = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [properties, setProperties] = useState([]);
  const [newPayment, setNewPayment] = useState({
    property: "",
    otherProperty: "",
    management: "",
    checkNumber: "",
    companyPaid: "",
    otherCompanyPaid: "",
    date: "",
    amount: "",
    memo: "",
    invoiceNumber: "",
    status: "Open", // ✅ default
  });
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    const fetchPayments = async () => {
      try {
        const paymentsRef = collection(db, "paymentsReceived");
        const querySnapshot = await getDocs(paymentsRef);
        const paymentsList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Sort: Open on top, then by date (optional)
        paymentsList.sort((a, b) => {
          if (a.status === "Open" && b.status !== "Open") return -1;
          if (a.status !== "Open" && b.status === "Open") return 1;
          return 0;
        });

        setPayments(paymentsList);
      } catch (error) {
        console.error("Error fetching payments:", error);
      }
    };

    const fetchProperties = async () => {
      try {
        const propertiesRef = collection(db, "properties");
        const querySnapshot = await getDocs(propertiesRef);
        const propertiesList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          propertyName: doc.data().propertyName, // Ensure PropertyName is fetched
          ...doc.data(),
        }));
        propertiesList.sort((a, b) => a.propertyName.localeCompare(b.propertyName)); // Sort properties alphabetically
        setProperties(propertiesList);
      } catch (error) {
        console.error("Error fetching properties:", error);
      }
    };

    fetchPayments();
    fetchProperties();
  }, []);

  useEffect(() => {
    const selected = properties.find(
      (p) => p.propertyName === newPayment.property
    );
    if (selected) {
      setNewPayment((prev) => ({
        ...prev,
        management: selected.management || "",
      }));
    } else {
      setNewPayment((prev) => ({
        ...prev,
        management: "",
      }));
    }
  }, [newPayment.property, properties]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewPayment((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, "paymentsReceived"), {
        ...newPayment,
        status: "Open", // ✅ force default on new add
      });
      setPayments([...payments, { ...newPayment, status: "Open" }]);
      setShowForm(false);
      setNewPayment({
        property: "",
        otherProperty: "",
        management: "",
        checkNumber: "",
        companyPaid: "",
        otherCompanyPaid: "",
        date: "",
        amount: "",
        memo: "",
        invoiceNumber: "",
        status: "Open", // Reset status
      }); // ✅ Reset form fields
    } catch (error) {
      console.error("Error adding payment:", error);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setNewPayment(selectedPayment);
  };

  const handleSave = async () => {
    try {
      const paymentRef = doc(db, "paymentsReceived", selectedPayment.id);
      await updateDoc(paymentRef, newPayment);
      setPayments(payments.map(payment => payment.id === selectedPayment.id ? { ...payment, ...newPayment } : payment));
      setSelectedPayment({ ...selectedPayment, ...newPayment });
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating payment:", error);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, "paymentsReceived", id));
      setPayments(payments.filter(payment => payment.id !== id));
      setSelectedPayment(null);
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error("Error deleting payment:", error);
    }
  };

  return (
    <div className="payments-container">
      <button className="blue-button" onClick={() => setShowForm(true)}>
        Payment
      </button>
      {showForm && (
        <>
          <div className="modal-overlay" onClick={() => setShowForm(false)}></div>
          <div className="modal">
            <h3>Add Payment</h3>
            <form className="form-container" onSubmit={handleSubmit}>
              <label>Property:</label>
              <select
                name="property"
                value={newPayment.property}
                onChange={handleInputChange}
                required
              >
                <option value="">Select Property</option>
                {properties.map((property) => (
                  <option key={property.id} value={property.propertyName}>
                    {property.propertyName}
                  </option>
                ))}
                <option value="Other">Other</option>
              </select>
              {newPayment.property === "Other" && (
                <>
                  <label>Other Property:</label>
                  <input
                    type="text"
                    name="otherProperty"
                    value={newPayment.otherProperty}
                    onChange={handleInputChange}
                  />
                </>
              )}
              <label>Management:</label>
              <input
                type="text"
                name="management"
                value={newPayment.management}
                onChange={handleInputChange}
                disabled
              />
              <label>Check Number:</label>
              <input
                type="text"
                name="checkNumber"
                value={newPayment.checkNumber}
                onChange={handleInputChange}
                required
              />
              <label>Company Paid:</label>
              <select
                name="companyPaid"
                value={newPayment.companyPaid}
                onChange={handleInputChange}
                required
              >
                <option value="">Select Company</option>
                <option value="Dynasty Community Services">
                  Dynasty Community Services
                </option>
                <option value="JMC Contracting">JMC Contracting</option>
                <option value="OGR Construction">OGR Construction</option>
                <option value="Other">Other</option>
              </select>
              {newPayment.companyPaid === "Other" && (
                <>
                  <label>Other Company Paid:</label>
                  <input
                    type="text"
                    name="otherCompanyPaid"
                    value={newPayment.otherCompanyPaid}
                    onChange={handleInputChange}
                  />
                </>
              )}
              <label>Date:</label>
              <input
                type="date"
                name="date"
                value={newPayment.date}
                onChange={handleInputChange}
                required
              />
              <label>Amount:</label>
              <input
                type="number"
                name="amount"
                value={newPayment.amount}
                onChange={handleInputChange}
                required
              />
              <label>Memo:</label>
              <textarea
                name="memo"
                value={newPayment.memo}
                onChange={handleInputChange}
              />
              <label>Invoice #:</label>
              <input
                type="text"
                name="invoiceNumber"
                value={newPayment.invoiceNumber}
                onChange={handleInputChange}
              />
              <div className="modal-buttons">
                <button type="submit" className="blue-button">
                  Save
                </button>
                <button
                  type="button"
                  className="green-button"
                  onClick={() => setShowForm(false)}
                >
                  Close
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Property</th>
              <th>Management</th>
              <th>Check Number</th>
              <th>Company Paid</th>
              <th>Date</th>
              <th>Amount</th>
              <th>Memo</th>
              <th>Invoice #</th> {/* Add Invoice # column */}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((payment) => (
              <tr
                key={payment.id}
                style={{
                  backgroundColor: payment.status === "Closed" ? "#d4edda" : "inherit", // Light green for Closed payments
                }}
              >
                <td>{payment.property === "Other" ? payment.otherProperty : payment.property}</td>
                <td>{payment.management}</td>
                <td>{payment.checkNumber}</td>
                <td>{payment.companyPaid === "Other" ? payment.otherCompanyPaid : payment.companyPaid}</td>
                <td>{payment.date}</td>
                <td>{payment.amount}</td>
                <td>{payment.memo}</td>
                <td>{payment.invoiceNumber}</td>
                <td>
                  <button
                    className="blue-button"
                    onClick={() => setSelectedPayment(payment)}
                  >
                    Details
                  </button>

                  {payment.status !== "Closed" && (
                    <button
                      className="greensmall-button"
                      onClick={async () => {
                        const paymentRef = doc(db, "paymentsReceived", payment.id);
                        try {
                          await updateDoc(paymentRef, { status: "Closed" });

                          // Update UI state
                          const updated = payments.map((p) =>
                            p.id === payment.id ? { ...p, status: "Closed" } : p
                          );

                          // Re-sort so Closed goes to bottom
                          updated.sort((a, b) => {
                            if (a.status === "Open" && b.status !== "Open") return -1;
                            if (a.status !== "Open" && b.status === "Open") return 1;
                            return 0;
                          });

                          setPayments(updated);
                        } catch (err) {
                          console.error("Error closing payment:", err);
                        }
                      }}
                    >
                      ADD QB
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedPayment && (
        <>
          <div className="modal-overlay" onClick={() => setSelectedPayment(null)}></div>
          <div className="modal">
            {isEditing ? (
              <div className="form-container">
                <h3>Edit Payment</h3>
                <label>Property:</label>
                <select
                  name="property"
                  value={newPayment.property}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Property</option>
                  {properties.map((property) => (
                    <option key={property.id} value={property.propertyName}>
                      {property.propertyName}
                    </option>
                  ))}
                  <option value="Other">Other</option>
                </select>
                {newPayment.property === "Other" && (
                  <>
                    <label>Other Property:</label>
                    <input
                      type="text"
                      name="otherProperty"
                      value={newPayment.otherProperty}
                      onChange={handleInputChange}
                    />
                  </>
                )}
                <label>Management:</label>
                <input
                  type="text"
                  name="management"
                  value={newPayment.management}
                  onChange={handleInputChange}
                  disabled
                />
                <label>Check Number:</label>
                <input
                  type="text"
                  name="checkNumber"
                  value={newPayment.checkNumber}
                  onChange={handleInputChange}
                  required
                />
                <label>Company Paid:</label>
                <select
                  name="companyPaid"
                  value={newPayment.companyPaid}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Company</option>
                  <option value="Dynasty Community Services">
                    Dynasty Community Services
                  </option>
                  <option value="JMC Contracting">JMC Contracting</option>
                  <option value="OGR Construction">OGR Construction</option>
                  <option value="Other">Other</option>
                </select>
                {newPayment.companyPaid === "Other" && (
                  <>
                    <label>Other Company Paid:</label>
                    <input
                      type="text"
                      name="otherCompanyPaid"
                      value={newPayment.otherCompanyPaid}
                      onChange={handleInputChange}
                    />
                  </>
                )}
                <label>Date:</label>
                <input
                  type="date"
                  name="date"
                  value={newPayment.date}
                  onChange={handleInputChange}
                  required
                />
                <label>Amount:</label>
                <input
                  type="number"
                  name="amount"
                  value={newPayment.amount}
                  onChange={handleInputChange}
                  required
                />
                <label>Memo:</label>
                <textarea
                  name="memo"
                  value={newPayment.memo}
                  onChange={handleInputChange}
                />
                <label>Invoice #:</label>
                <input
                  type="text"
                  name="invoiceNumber"
                  value={newPayment.invoiceNumber}
                  onChange={handleInputChange}
                />
                <label>Status:</label>
                <select
                  name="status"
                  value={newPayment.status}
                  onChange={handleInputChange}
                  required
                >
                  <option value="Open">Open</option>
                  <option value="Closed">Closed</option>
                </select>
                <div className="modal-buttons">
                  <button onClick={handleSave} className="blue-button">
                    Save
                  </button>
                  <button onClick={handleCancel} className="green-button">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="popup-content">
                <h3>Payment Details</h3>
                <p>
                  <strong>Property:</strong>{" "}
                  {selectedPayment.property === "Other"
                    ? selectedPayment.otherProperty
                    : selectedPayment.property}
                </p>
                <p>
                  <strong>Management:</strong> {selectedPayment.management}
                </p>
                <p>
                  <strong>Check Number:</strong> {selectedPayment.checkNumber}
                </p>
                <p>
                  <strong>Company Paid:</strong>{" "}
                  {selectedPayment.companyPaid === "Other"
                    ? selectedPayment.otherCompanyPaid
                    : selectedPayment.companyPaid}
                </p>
                <p>
                  <strong>Date:</strong> {selectedPayment.date}
                </p>
                <p>
                  <strong>Amount:</strong> {selectedPayment.amount}
                </p>
                <p>
                  <strong>Memo:</strong> {selectedPayment.memo}
                </p>
                <p>
                  <strong>Invoice #:</strong> {selectedPayment.invoiceNumber}
                </p>
                <p>
                  <strong>Status:</strong> {selectedPayment.status}
                </p> {/* Add Status to details popup */}
                <div className="modal-buttons">
                  <button onClick={handleEdit} className="blue-button">
                    Edit
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="red-button"
                  >
                    Delete
                  </button>
                  <button
                    onClick={() => setSelectedPayment(null)}
                    className="green-button"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Are you sure you want to delete?</h3>
            <div className="modal-buttons">
              <button
                onClick={() => handleDelete(selectedPayment.id)}
                className="red-button"
              >
                Yes
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="green-button"
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentsReceived;